import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { AddSingleProductToSelectionListResponseOut, UpdateProductOptions } from '../../store/api';
import { SOURCE_SELECTIONLIST } from '../../store/constants';
import { ListSourceType } from '../../../models/ListSourceType';
import { invalidateSelectionListAggregations } from '../../shop/selectionlists/useSelectionListAggregations';
import { invalidateCartAggregations } from '../../shop/cart/useCartAggregations';
import { invalidateCartSummaryQuery } from '../shopping-cart/useCartSummaryQuery';
import { invalidateShoppingCartProducts } from '../shopping-cart/useShoppingCartProducts';
import { invalidateSelectionListProducts } from '../../shop/selectionlists/api/useSelectionListProducts';
import {
    moveSingleProductFromCartToList,
    MoveSingleProductFromShoppingCartRequest
} from './moveSingleProductFromCartToList';
import {
    moveSingleProductFromListToList,
    MoveSingleProductFromSelectionListRequest
} from './moveSingleProductFromListToList';
import { singleUpdateToList } from './singleUpdateToList';
import { invalidateCartGroupedItems } from '../shopping-cart/useCartGroupedItems';
import { invalidateSelectionListGroupedItems } from './useSelectionListGroupedItems';
import { invalidateSelectionListSummary } from './useSelectionListSummary';
import { convertUsageConditionToUsageConditionDto } from '../../shared/utils/convertLineItemKey';

type GetRequestParams = (
    | {
          source: typeof ListSourceType.CART;
      }
    | {
          source: typeof ListSourceType.SELECTION_LIST;
          sourceSelectionListId: string;
      }
) & {
    selectionListId: string;
    options: UpdateProductOptions;
    performChecks: boolean;
};

export const buildMoveProductCallback = (
    { selectionListId, options, performChecks, ...sourceParams }: GetRequestParams,
    t: TFunction
): (() => Promise<AddSingleProductToSelectionListResponseOut>) => {
    return () => {
        try {
            const params = { performChecks };
            const request: MoveSingleProductFromShoppingCartRequest = {
                usageCondition: convertUsageConditionToUsageConditionDto(options.price),
                subscriptionPeriodType: options.subscriptionPeriodType
            };
            if (sourceParams.source === SOURCE_SELECTIONLIST) {
                const requestWithSourceList: MoveSingleProductFromSelectionListRequest = {
                    ...request,
                    sourceSelectionListId: sourceParams.sourceSelectionListId
                };
                return moveSingleProductFromListToList({
                    selectionListId,
                    productId: options.id,
                    body: requestWithSourceList,
                    params
                });
            }
            return moveSingleProductFromCartToList({ selectionListId, productId: options.id, body: request, params });
        } catch (e) {
            throw new Error(t('notifications.buildAddProductsCallback.error'));
        }
    };
};

type Props =
    | {
          source: typeof ListSourceType.CART;
      }
    | {
          source: typeof ListSourceType.SELECTION_LIST;
          sourceSelectionListId: string;
      };

export const useMoveSingleToSelectionList = (sourceParams: Props) => {
    const { t } = useTranslation();
    const [isLoading, setLoading] = useState(false);

    return {
        isLoading,
        moveToList: (options: UpdateProductOptions, listId: string, performChecks: boolean) => {
            const apiCallback = buildMoveProductCallback(
                {
                    selectionListId: listId,
                    options,
                    performChecks,
                    ...sourceParams
                },
                t
            );

            const callback = () => {
                if (sourceParams.source === ListSourceType.SELECTION_LIST) {
                    invalidateSelectionListAggregations(sourceParams.sourceSelectionListId).then();
                    invalidateSelectionListProducts(sourceParams.sourceSelectionListId).then();
                    invalidateSelectionListSummary(sourceParams.sourceSelectionListId).then();
                    invalidateSelectionListGroupedItems(sourceParams.sourceSelectionListId).then();
                } else {
                    invalidateCartAggregations().then();
                    invalidateCartSummaryQuery().then();
                    invalidateShoppingCartProducts().then();
                    invalidateCartGroupedItems().then();
                }
            };

            return singleUpdateToList({
                setLoading,
                notificationMessage: t('notifications.getMessageFromActionList.move', { productTitle: options.title }),
                apiCallback,
                callback
            });
        }
    };
};
